import React, { useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFilePdf, FaFileInvoiceDollar, FaCheckCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/components/DeclararRentaTab.css';

/**
 * Componente para gestionar Declaraciones de Renta
 * Diseñado de manera modular para ser usado por agentes autónomos
 *
 * @param {Object} props
 * @param {Array} props.declaraciones - Lista de declaraciones de renta
 * @param {number} props.selectedYear - Año seleccionado
 * @param {boolean} props.loading - Estado de carga
 * @param {Function} props.onCreate - Callback para crear nueva declaración
 * @param {Function} props.onUpdate - Callback para actualizar declaración
 * @param {Function} props.onDelete - Callback para eliminar declaración
 */
const DeclararRentaTab = ({
    declaraciones = [],
    selectedYear,
    loading = false,
    onCreate,
    onUpdate,
    onDelete
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingDeclaracion, setEditingDeclaracion] = useState(null);
    const [formData, setFormData] = useState({
        tipo_contribuyente: '',
        rut_contribuyente: '',
        nombre_contribuyente: '',
        ingresos_totales: '',
        gastos_aceptados: '',
        base_imponible: '',
        impuesto_primera_categoria: '',
        creditos: '',
        ppmo_pagados: '',
        resultado: '',
        fecha_declaracion: '',
        folio: '',
        estado: 'BORRADOR'
    });

    /**
     * Tipos de contribuyente disponibles
     * Puede ser configurado externamente
     */
    const tiposContribuyente = [
        { value: 'PERSONA_NATURAL', label: 'Persona Natural' },
        { value: 'EMPRESA_INDIVIDUAL', label: 'Empresa Individual' },
        { value: 'SPA', label: 'Sociedad por Acciones (SpA)' },
        { value: 'LTDA', label: 'Sociedad Limitada (Ltda)' },
        { value: 'SA', label: 'Sociedad Anónima (SA)' }
    ];

    const handleOpenModal = (declaracion = null) => {
        if (declaracion) {
            setEditingDeclaracion(declaracion);
            setFormData({
                tipo_contribuyente: declaracion.tipo_contribuyente || '',
                rut_contribuyente: declaracion.rut_contribuyente || '',
                nombre_contribuyente: declaracion.nombre_contribuyente || '',
                ingresos_totales: declaracion.ingresos_totales || '',
                gastos_aceptados: declaracion.gastos_aceptados || '',
                base_imponible: declaracion.base_imponible || '',
                impuesto_primera_categoria: declaracion.impuesto_primera_categoria || '',
                creditos: declaracion.creditos || '',
                ppmo_pagados: declaracion.ppmo_pagados || '',
                resultado: declaracion.resultado || '',
                fecha_declaracion: declaracion.fecha_declaracion || '',
                folio: declaracion.folio || '',
                estado: declaracion.estado || 'BORRADOR'
            });
        } else {
            setEditingDeclaracion(null);
            setFormData({
                tipo_contribuyente: '',
                rut_contribuyente: '',
                nombre_contribuyente: '',
                ingresos_totales: '',
                gastos_aceptados: '',
                base_imponible: '',
                impuesto_primera_categoria: '',
                creditos: '',
                ppmo_pagados: '',
                resultado: '',
                fecha_declaracion: '',
                folio: '',
                estado: 'BORRADOR'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingDeclaracion(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: value
            };

            // Calcular automáticamente si cambian los montos
            if (['ingresos_totales', 'gastos_aceptados', 'impuesto_primera_categoria', 'creditos', 'ppmo_pagados'].includes(name)) {
                const ingresos = parseFloat(newData.ingresos_totales) || 0;
                const gastos = parseFloat(newData.gastos_aceptados) || 0;
                const base = ingresos - gastos;
                newData.base_imponible = base.toString();

                const impuesto = parseFloat(newData.impuesto_primera_categoria) || 0;
                const creditos = parseFloat(newData.creditos) || 0;
                const ppmo = parseFloat(newData.ppmo_pagados) || 0;
                const resultado = impuesto - creditos - ppmo;
                newData.resultado = resultado.toString();
            }

            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validación básica
        if (!formData.tipo_contribuyente || !formData.rut_contribuyente) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos requeridos',
                text: 'Debe completar tipo de contribuyente y RUT'
            });
            return;
        }

        try {
            if (editingDeclaracion) {
                await onUpdate(editingDeclaracion.id, formData);
                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Declaración de renta actualizada exitosamente'
                });
            } else {
                await onCreate(formData);
                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Declaración de renta creada exitosamente'
                });
            }
            handleCloseModal();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al guardar declaración de renta'
            });
        }
    };

    const handleDelete = async (declaracion) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await onDelete(declaracion.id);
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'Declaración de renta eliminada exitosamente'
                });
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Error al eliminar declaración de renta'
                });
            }
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL');
    };

    const getEstadoBadgeClass = (estado) => {
        const estados = {
            'BORRADOR': 'estado-borrador',
            'ENVIADO': 'estado-enviado',
            'ACEPTADO': 'estado-aceptado',
            'RECHAZADO': 'estado-rechazado'
        };
        return estados[estado] || 'estado-borrador';
    };

    return (
        <div className="declarar-renta-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaFileInvoiceDollar className="title-icon" />
                    <h2>Declaración de Renta - Año Tributario {selectedYear}</h2>
                </div>
                <button
                    className="btn-nuevo"
                    onClick={() => handleOpenModal()}
                    disabled={loading}
                >
                    <FaPlus /> Nueva Declaración de Renta
                </button>
            </div>

            {/* Información del año tributario */}
            <div className="info-card">
                <h3>Año Tributario {selectedYear}</h3>
                <p>Corresponde a rentas del año comercial {selectedYear - 1}</p>
                <p className="text-muted">
                    Plazo de declaración: Abril {selectedYear}
                </p>
            </div>

            {/* Tabla de declaraciones */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : declaraciones.length === 0 ? (
                    <div className="empty-message">
                        <FaFileInvoiceDollar className="empty-icon" />
                        <p>No hay declaraciones de renta para el año {selectedYear}</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            <FaPlus /> Crear Primera Declaración
                        </button>
                    </div>
                ) : (
                    <table className="declaraciones-table">
                        <thead>
                            <tr>
                                <th>RUT</th>
                                <th>Contribuyente</th>
                                <th>Tipo</th>
                                <th>Base Imponible</th>
                                <th>Resultado</th>
                                <th>Folio</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {declaraciones.map((declaracion) => (
                                <tr key={declaracion.id}>
                                    <td>{declaracion.rut_contribuyente}</td>
                                    <td>{declaracion.nombre_contribuyente}</td>
                                    <td>{declaracion.tipo_contribuyente}</td>
                                    <td className="text-right">{formatCurrency(declaracion.base_imponible)}</td>
                                    <td className={`text-right ${parseFloat(declaracion.resultado) > 0 ? 'text-danger' : 'text-success'}`}>
                                        {formatCurrency(declaracion.resultado)}
                                    </td>
                                    <td>{declaracion.folio || '-'}</td>
                                    <td>
                                        <span className={`estado-badge ${getEstadoBadgeClass(declaracion.estado)}`}>
                                            {declaracion.estado}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleOpenModal(declaracion)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(declaracion)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                        <button
                                            className="btn-icon btn-pdf"
                                            title="Descargar F22"
                                        >
                                            <FaFilePdf />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingDeclaracion ? 'Editar' : 'Nueva'} Declaración de Renta</h3>
                            <button className="btn-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h4>Datos del Contribuyente</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="tipo_contribuyente">Tipo *</label>
                                        <select
                                            id="tipo_contribuyente"
                                            name="tipo_contribuyente"
                                            value={formData.tipo_contribuyente}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Seleccione...</option>
                                            {tiposContribuyente.map(tipo => (
                                                <option key={tipo.value} value={tipo.value}>
                                                    {tipo.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="rut_contribuyente">RUT *</label>
                                        <input
                                            type="text"
                                            id="rut_contribuyente"
                                            name="rut_contribuyente"
                                            value={formData.rut_contribuyente}
                                            onChange={handleInputChange}
                                            placeholder="12.345.678-9"
                                            required
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="nombre_contribuyente">Nombre/Razón Social</label>
                                        <input
                                            type="text"
                                            id="nombre_contribuyente"
                                            name="nombre_contribuyente"
                                            value={formData.nombre_contribuyente}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Cálculo de Renta</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="ingresos_totales">Ingresos Totales</label>
                                        <input
                                            type="number"
                                            id="ingresos_totales"
                                            name="ingresos_totales"
                                            value={formData.ingresos_totales}
                                            onChange={handleInputChange}
                                            step="0.01"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="gastos_aceptados">Gastos Aceptados</label>
                                        <input
                                            type="number"
                                            id="gastos_aceptados"
                                            name="gastos_aceptados"
                                            value={formData.gastos_aceptados}
                                            onChange={handleInputChange}
                                            step="0.01"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="base_imponible">Base Imponible (calculada)</label>
                                        <input
                                            type="number"
                                            id="base_imponible"
                                            name="base_imponible"
                                            value={formData.base_imponible}
                                            readOnly
                                            className="readonly-field"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="impuesto_primera_categoria">Impuesto 1ª Categoría</label>
                                        <input
                                            type="number"
                                            id="impuesto_primera_categoria"
                                            name="impuesto_primera_categoria"
                                            value={formData.impuesto_primera_categoria}
                                            onChange={handleInputChange}
                                            step="0.01"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="creditos">Créditos</label>
                                        <input
                                            type="number"
                                            id="creditos"
                                            name="creditos"
                                            value={formData.creditos}
                                            onChange={handleInputChange}
                                            step="0.01"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="ppmo_pagados">PPMO Pagados</label>
                                        <input
                                            type="number"
                                            id="ppmo_pagados"
                                            name="ppmo_pagados"
                                            value={formData.ppmo_pagados}
                                            onChange={handleInputChange}
                                            step="0.01"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="resultado">Resultado (calculado)</label>
                                        <input
                                            type="number"
                                            id="resultado"
                                            name="resultado"
                                            value={formData.resultado}
                                            readOnly
                                            className={`readonly-field ${parseFloat(formData.resultado) > 0 ? 'text-danger' : 'text-success'}`}
                                        />
                                        <small className="form-help">
                                            {parseFloat(formData.resultado) > 0 ? 'A pagar' : 'A favor'}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Información de Declaración</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="fecha_declaracion">Fecha Declaración</label>
                                        <input
                                            type="date"
                                            id="fecha_declaracion"
                                            name="fecha_declaracion"
                                            value={formData.fecha_declaracion}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="folio">Folio</label>
                                        <input
                                            type="text"
                                            id="folio"
                                            name="folio"
                                            value={formData.folio}
                                            onChange={handleInputChange}
                                            placeholder="Folio SII"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="estado">Estado</label>
                                        <select
                                            id="estado"
                                            name="estado"
                                            value={formData.estado}
                                            onChange={handleInputChange}
                                        >
                                            <option value="BORRADOR">Borrador</option>
                                            <option value="ENVIADO">Enviado</option>
                                            <option value="ACEPTADO">Aceptado</option>
                                            <option value="RECHAZADO">Rechazado</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    <FaCheckCircle /> {editingDeclaracion ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeclararRentaTab;
